import React, { useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  makeStyles,
  CircularProgress,
  Grid,
  Typography,
  Box,
  TextField,
  MenuItem,
  InputAdornment,
  IconButton,
  Tooltip,
  Button,
  Select,
  FormControl,
} from "@material-ui/core";
import moment from "moment";
import { Clear, Refresh, ChevronLeft, ChevronRight } from "@material-ui/icons";
import SearchIcon from "@material-ui/icons/Search";
import ClearIcon from "@material-ui/icons/Clear";
import { convertToLocaleDateTime } from "../functionalComponents/FormatDateTimeToLocale";
import PaginationInput from "../models/pagination/PaginationInput";
import { useDispatch } from "react-redux";
import { FETCH_PO_LINE_AUDIT_DETAILS_NEW_LOADING } from "../../../containers/actions/types";
import { fetchPOLineAuditDetailsNew } from "../../../containers/actions/common/purchaseOrderNew";

const useStyles = makeStyles((theme) => ({
  tableHeader: {
    backgroundColor: "#FFE4C2", // Light orange color for the header
    // border: "1px solid #ffab91",
    fontWeight: "bold",
  },
  tableContainer: {
    marginTop: theme.spacing(3),
    maxHeight: 450, // Set the max height of the table container (adjust as needed)
    overflowY: "auto", // Enable vertical scrolling
  },
  selectDropdown: {
    "& .MuiSelect-select": {
      backgroundColor: "#F0F0F0 !important",
      "&:focus": {
        backgroundColor: "#F0F0F0 !important",
      },
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "transparent !important",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "transparent !important",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "transparent !important",
    },
    // Override menu item styles globally for this component
    "& .MuiMenuItem-root": {
      backgroundColor: "#F0F0F0 !important",
      "&:hover": {
        backgroundColor: "#F0F0F0 !important",
      },
      "&.Mui-selected": {
        backgroundColor: "#F0F0F0 !important",
        "&:hover": {
          backgroundColor: "#F0F0F0 !important",
        },
      },
      "&.Mui-focusVisible": {
        backgroundColor: "#F0F0F0 !important",
      },
    },
  },
  menuItem: {
    "&:hover": {
      backgroundColor: "#F0F0F0 !important",
    },
    "&.Mui-selected": {
      backgroundColor: "#F0F0F0 !important",
      "&:hover": {
        backgroundColor: "#F0F0F0 !important",
      },
    },
    "&.Mui-focusVisible": {
      backgroundColor: "#F0F0F0 !important",
    },
  },
}));

export default function AuditReportTableNew({
  poLineAuditsNewData,
  poLineAuditsNewDataLoading,
  rowInfo
}) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [searchField, setSearchField] = useState("Select search by");
  const [searchValue, setSearchValue] = useState("");
  const [disabled, setDisabled] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleSearchField = (event) => {
    const selectedValue = event.target.value;
    setSearchField(selectedValue);
    setSearchValue(""); // Reset value when search type changes
    
    // Enable/disable the search input based on selection
    setDisabled(selectedValue === "Select search by");
  };

  const handleSearchClick = () => {

  };

  const handleClearSearch = () => {
    setSearchValue("");
  };

  const handleClearFilters = () => {
    setSearchValue("");
  };


  // Filter records based on search criteria
  const filteredData = useMemo(() => {
    if (!searchValue.trim())
      return poLineAuditsNewData?.data?.poLineAuditTrail || [];

    return poLineAuditsNewData?.data?.poLineAuditTrail.filter((row) =>
      row[searchField.toLowerCase()]
        ?.toLowerCase()
        .includes(searchValue.toLowerCase())
    );
  }, [searchValue, searchField, poLineAuditsNewData]);

  const totalRecords = filteredData?.length || 0; // Calculate total records
  const totalPages = Math.ceil(totalRecords / rowsPerPage); // Calculate total pages

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredData.slice(startIndex, endIndex); // Slice filtered data
  }, [filteredData, currentPage, rowsPerPage]);

  // const handleRowsPerPageChange = (event) => {
  //   const newRowsPerPage = event.target.value;
  //   setRowsPerPage(newRowsPerPage);

  //   setCurrentPage(1); //Reset to first page
  // };

  // let showPagination = filteredData?.totalNoOfRecords > 0;

  //   const handlePageChange = (newPage) => {
  //     setCurrentPage(newPage);
  //   };

  const handleRefresh = () => {
    dispatch({
      type: FETCH_PO_LINE_AUDIT_DETAILS_NEW_LOADING,
      payload: true,
    });
    dispatch(
      fetchPOLineAuditDetailsNew({
        schrockenSLID: rowInfo?.scheduleLineDetails?.schrockenSLID || "",
        viewRecentChanges: false,
      })
    );
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10)); // Convert to number
    setCurrentPage(1); // Reset to first page
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  return (
    <>
      <Grid
        style={{
          display: "flex",
          flexDirection: "row",
          width: "95vw",
          marginTop: "1rem",
        }}
      >
        <Grid
          style={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            flexDirection: "row",
            gap: "2rem",
          }}
        >
          <Grid
            item
            xs={12}
            sm={4}
            md={4}
            style={{ display: "flex", alignItems: "center", width: "60vw" }}
          >
            <Box
              style={{ width: "100%", display: "flex", alignItems: "center" }}
            >
              {/* Dropdown for selecting search criteria */}
              <TextField
                size="small"
                select
                value={searchField}
                onChange={handleSearchField}
                variant="outlined"
                className={classes.selectDropdown}
                style={{
                  minWidth: 30,
                  border: "1px solid black",
                  borderRight: "none",
                  borderRight: "none",
                  borderRadius: "0.3rem 0 0 0.3rem",
                  backgroundColor: "#F0F0F0",
                }}
                SelectProps={{
                  MenuProps: {
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                  },
                }}
              >
                <MenuItem value="Select search by" className={classes.menuItem}>
                  Select search by
                </MenuItem>
                <MenuItem value="Event" className={classes.menuItem}>
                  Event
                </MenuItem>
                <MenuItem value="Month" className={classes.menuItem}>
                  Month
                </MenuItem>
              </TextField>

              {/* Input field (only if "Event" or "Month" is selected) */}

              <TextField
                size="small"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onBlur={(e)=>{
                  const { value} = e.target
                  if(typeof value === 'string' && value !== value.trim()){
                    setSearchValue(value.trim())
                  }
                }}
                onKeyDown={(e) => {
                          const { value } = e.target;
                          if(e.key === "Enter"){
                            setSearchValue(value.trim())
                            handleSearchClick()
                          }
                        }}
                variant="outlined"
                placeholder={disabled ? "Select search criteria first" : `Search by ${searchField.toLowerCase()}`}
                disabled={disabled}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {searchValue && (
                        <Tooltip title="Clear search" aria-label="clear">
                          <Clear
                            fontSize="small"
                            onClick={handleClearSearch}
                            style={{ cursor: "pointer", color: "dimgray" }}
                          />
                        </Tooltip>
                      )}
                      <IconButton
                        size="small"
                        disabled={false}
                        style={{
                          backgroundColor: "#EB5E28",
                          color: "white",
                          padding: "8.7px",
                          translate: "0.84rem",
                          borderRadius: "0 0.3rem 0.3rem 0",
                          padding: "8.7px",
                          translate: "0.84rem",
                          // border: "1px solid black", // Add border to button
                          borderLeft: "none",
                          height: "40px",
                          width: "38px",
                          minWidth: "32px",
                          minHeight: "32px"
                        }}
                        onClick={handleSearchClick}
                      >
                        <SearchIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                style={{
                  flex: 1,
                  border: "1px solid black",
                  borderLeft: "none",
                  borderRight: "0px",
                  borderRadius: "0 0.3rem 0.3rem 0",
                }}
              />
            </Box>
          </Grid>

          {/* <Grid
            style={{
              display: "flex",
              alignItems: "flex-start",
              // gap: "20px",
              flexDirection: "column",
              // marginTop: "1.5rem",
            }}
          >
            <Button
              variant="contained"
              // color="primary"
              disableElevation
              disableRipple
              disableFocusRipple
              disabled={disabled} // Correct way to disable the button
              style={{
                backgroundColor: disabled ? "#f4b799" : "#ec6535", // Keep background color
                color: "#fff",
                borderRadius: "2rem",
                textTransform: "none",
              }}
            //  onClick={handleSaveFilters}
            >
              Save Filters
            </Button>
          </Grid> */}

          {/* Clear filter */}
          {/* <Grid
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: "20px",
              flexDirection: "column",
              // marginTop: "1.5rem",
            }}
          >
            <Typography
              variant="caption"
              style={{
                justifyContent: "end",
                fontSize: 12,
                color: "blue",
                marginLeft: "5px",
                textDecoration: "underline",
              }}
              className={classes.clearText}
              onClick={handleClearFilters}
            >
              Clear All Filters
            </Typography>
          </Grid> */}
        </Grid>

        <Grid
          style={{
            display: "flex",
            justifyContent: "flex-end",
            flexDirection: "column",
            alignItems: "flex-end",
            flex: 1,
            marginLeft: "auto",
           
          }}
        >
          <Grid
            style={{
              display: "flex",
              justifyContent: "flex-end",
              flexDirection: "column",
              alignItems: "flex-end",
              paddingTop: "8px", // Add this line for upper padding
            }}
          >
            {/* Last synced and refresh section */}
            <Grid
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginBottom: "8px", // Add some bottom margin for spacing
              }}
            >
              <Typography
                variant="body2"
                style={{
                  color: "gray",
                  marginRight: "8px",
                  whiteSpace: "nowrap",
                }}
              >
                {`Last synced @ ${moment(new Date()).format(
                  "HH:mm:ss; DD MMMM, YYYY"
                )}`}
              </Typography>
              <Grid>
                <Typography
                  variant="body2"
                  color="primary"
                  style={{
                    textDecoration: "underline",
                    fontStyle: "italic",
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                  onClick={handleRefresh}
                >
                  Refresh
                  <Refresh color="primary" fontSize="small" />
                </Typography>
              </Grid>
            </Grid>

            {/* PER PAGE section */}
            <Grid
              style={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                flexDirection: "row",
                gap: "2rem",
                paddingTop: "8px",
              }}
            >
              <Typography
                style={{
                  fontWeight: "bold",
                  marginRight: "4px",
                  whiteSpace: "nowrap",
                }}
              >
                PER PAGE
              </Typography>
              <FormControl variant="outlined" size="small">
                <Select value={rowsPerPage} onChange={handleRowsPerPageChange}>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table stickyHeader>
          <TableHead
            style={{
              backgroundColor: "#FFE4C2 ",
              border: "1px solid #ffab91",
              zIndex: 10,
            }}
          >
            <TableRow>
              <TableCell className={classes.tableHeader}></TableCell>
              <TableCell className={`${classes.tableHeader} `}>
                Date & Time
              </TableCell>
              <TableCell className={classes.tableHeader}>Event</TableCell>
              <TableCell className={classes.tableHeader}>
                Event Description
              </TableCell>
              <TableCell align="center" className={classes.tableHeader}>
                PO Status
              </TableCell>
              <TableCell className={classes.tableHeader}>
                Organization
              </TableCell>
              <TableCell className={classes.tableHeader}>Role</TableCell>
              <TableCell className={classes.tableHeader}>Username</TableCell>
            </TableRow>
          </TableHead>
          <TableBody style={{ position: "relative", zIndex: 1 }}>
            {poLineAuditsNewDataLoading === true ? (
              <TableRow style={{ height: 150 }}>
                <TableCell colSpan={12}>
                  <div style={{ textAlign: "center" }}>
                    <CircularProgress color="primary" thickness={7} />
                  </div>
                </TableCell>
              </TableRow>
            ) : paginatedData.length > 0 ? (
              // poLineAuditsNewData?.data?.poLineAuditTrail?.map
              paginatedData?.map((row, index) => (
                <TableRow key={index}>
                  <TableCell
                    style={{ position: "relative" }}
                    // style={{ verticalAlign: "center", position: "relative" }}
                  >
                    <Box display="flex" alignItems="center" position="relative">
                      <Box
                        style={{
                          width: 12,
                          height: 12,
                          backgroundColor: "orangeRed",
                          borderRadius: "50%",
                          // marginRight: 10,
                          position: "relative",
                          zIndex: 2, // Keep above connector
                          overflow: "hidden",
                        }}
                      />

                      {index !== paginatedData.length - 1 && (
                        <Box
                          style={{
                            position: "absolute",
                            width: 1.5,
                            height: "7rem",
                            backgroundColor: "gray",
                            left: 5,
                            top: -12,
                            // bottom:-12,
                            // top: -19,
                            // top:5, // Start after the circle
                          }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {row?.lastUpdatedDate
                      ? `${moment(convertToLocaleDateTime(row?.lastUpdatedDate))
                          .format("DD-MMM-YYYY HH:mm:ss")
                          .toUpperCase()}`
                      : ""}
                  </TableCell>
                  <TableCell>{row?.event || ""} </TableCell>
                  <TableCell>{row?.eventDescription || ""}</TableCell>
                  <TableCell align="center">
                    {/* {row?.status || "NA"} */}

                    {row?.status ? (
                      <Box
                        style={{
                          backgroundColor:
                            row.status === "Created"
                              ? "#B4B4B4"
                              : row.status === "Under Confirmation"
                              ? "#E78A00"
                              : row.status === "Shared with CMO"
                              ? "#2196F3"
                              : row.status === "Confirmed"
                              ? "#329429"
                              : row.status === "Under Revision"
                              ? "#329429"
                              : row.status === "Revised"
                              ? "#329429"
                              : row.status === "Delivery Complete"
                              ? "#707070"
                              : row.status === "Approved"
                              ? "#329429"
                              // "Changed" and "Changes under review" are shown in green to stay
                              // consistent with how the PO Collaboration dashboard colors these statuses.
                              : row.status === "Changed"
                              ? "#329429"
                              : row.status === "Changes under review"
                              ? "#329429"
                              : row.status === "Rejected By CMO"
                              ? "#FF0000"
                              : "#1890FF", // Default Dark Grey
                          color: "#fff",
                          paddingLeft: "0.5rem",
                          paddingRight: "0.5rem",
                          paddingTop: "5px",
                          paddingBottom: "5px",
                          textAlign: "center",
                          whiteSpace: "nowrap",
                          width: "10rem",
                          borderRadius: "4px",
                          display: "inline-block",
                        }}
                      >
                        {row.status}
                      </Box>
                    ) : (
                      ""
                    )}
                  </TableCell>
                  <TableCell>{row?.organizationName || ""}</TableCell>
                  <TableCell>{row?.role || ""}</TableCell>
                  <TableCell>{row?.lastUpdatedBy || ""}</TableCell>
                </TableRow>
              ))
            ) : poLineAuditsNewData?.status === false ? (
              <TableRow
                style={{
                  border: "2px solid rgb(255,0,0)",
                  backgroundColor: "rgb(255,240,240)",
                  Opacity: 1,
                  textAlign: "center",
                  padding: "0.8rem",
                  fontSize: "0.875rem",
                }}
              >
                <TableCell colSpan={8} align="center">
                  {"Something went wrong. Please try again later."}
                </TableCell>
              </TableRow>
            ) : (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  No records found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* {showPagination === true &&
            fetchPurchaseOrderDataLoading === false && (
              <Grid
                container
                alignItems="center"
                justifyContent="center"
                style={{ paddingTop: "1rem" }}
              >
                <PaginationInput
                  totalPages={
                    purchaseOrderData?.numberOfPages
                      ? purchaseOrderData?.numberOfPages
                      : 0
                  }
                  handlePageChange={handlePageChange}
                  page={currentPage}
                />
              </Grid>
            )} */}

      {/* Pagination Controls */}
      {totalRecords > 0 && (
        <Grid
          container
          alignItems="center"
          justifyContent="center"
          style={{ paddingTop: "1rem" }}
        >
          <PaginationInput
            totalPages={totalPages}
            handlePageChange={handlePageChange}
            page={currentPage}
          />
        </Grid>
      )}
    </>
  );
}
